import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMytaverseEvent } from '../../../../providers';
import { useMytaStreams } from '../MytaStreamsProvider';
import { MytaverseLogger } from '@helpers/logger';
import {
  CATCHUP_FLOOR_PER_SEC,
  CATCHUP_RATE_PER_SEC,
  CINEMA_PHASES,
  ETA_FLOOR_SEC,
  ETA_GLIDE_TAU_SEC,
  MAX_FRAME_DELTA_SEC,
  MIN_PHASE_TAU_SEC,
  PHASE_TAIL_SEC,
  WORLD_ANNOUNCE_WAIT_MS,
  WORLD_SETTLE_MS,
} from './constants';

export interface ICinemaError {
  headline: string;
  reason: string;
}

export type CinemaProgressListener = (percent: number) => void;

interface IServerEta {
  key: string;
  valueSec: number;
  at: number;
}

/**
 * Drives the MytaStreams loading-cinema progress bar and reports when entry is possible.
 *
 * @returns `percent`/`subscribe` for the animated progress bar, `phaseIdx`/`etaSec` for the
 * loading copy, `ready`/`done` for gating entry, and `error`/`hintOverride`/`onSpot` for
 * messaging.
 */
export const useCinemaProgress = () => {
  const {
    leaseStatus,
    leasePhase,
    dataChannelOpened,
    mytaLoading,
    connectedSlot,
    lease,
  } = useMytaStreams();
  const { currentRoom, teleportFinished } = useMytaverseEvent();

  // The stream, and then the world. `streamUp` is the video track decoding into a known room — everything the
  // page needs, and not yet anything the user should be let into: UE is still placing the first runtime
  // objects behind it. `teleportFinished` is UE's own word that it is done (CLIENT_READY / TELEPORT_FINISHED
  // on this session, hooks/index.ts), and the loading runs until it arrives plus WORLD_SETTLE_MS.
  const streamUp = dataChannelOpened && !mytaLoading && !!currentRoom;

  const [announceWaitedOut, setAnnounceWaitedOut] = useState(false);
  const [settled, setSettled] = useState(false);

  const worldAnnounced = teleportFinished || announceWaitedOut;
  const ready = streamUp && worldAnnounced && settled;

  useEffect(() => {
    if (!streamUp || teleportFinished) {
      return;
    }

    const timeout = setTimeout(() => {
      MytaverseLogger.log(
        'UE never announced the world — finishing the loading on the stream alone',
        { waitedMs: WORLD_ANNOUNCE_WAIT_MS },
      );
      setAnnounceWaitedOut(true);
    }, WORLD_ANNOUNCE_WAIT_MS);

    return () => clearTimeout(timeout);
  }, [streamUp, teleportFinished]);

  useEffect(() => {
    if (!streamUp || !worldAnnounced) {
      return;
    }

    const timeout = setTimeout(() => setSettled(true), WORLD_SETTLE_MS);

    return () => clearTimeout(timeout);
  }, [streamUp, worldAnnounced]);

  // The GPU box behind this entry is a spot box — bought cheap and interruptible. Worth saying out loud on the
  // loading screen, because it is slower to get by construction (myta-streams D-49 buys spot before waking any
  // on-demand box) and an unexplained extra wait is indistinguishable from a hang. The lease is read first: the
  // provider clears `leasePhase` at the grant, so the two are never both set, and this keeps whichever answer
  // the server gave last. Absent on either means "no box chosen yet", never "an ordinary one".
  const onSpot = (lease?.market ?? leasePhase?.market) === 'spot';

  const phaseIdx = useMemo(() => {
    if (streamUp) {
      return 4;
    }

    if (dataChannelOpened) {
      return 3;
    }

    if (connectedSlot) {
      return 2;
    }

    if (leaseStatus === 'active') {
      return 1;
    }

    return 0;
  }, [streamUp, dataChannelOpened, connectedSlot, leaseStatus]);

  const error: ICinemaError | null = useMemo(() => {
    if (leaseStatus === 'error' || leaseStatus === 'unconfigured') {
      return {
        headline: 'We couldn’t start your experience',
        reason: 'Something went wrong while preparing your session',
      };
    }

    if (leasePhase?.phase === 'build_unsupported') {
      return {
        headline: 'This experience is temporarily unavailable',
        reason: 'It cannot start right now — please try again later',
      };
    }

    return null;
  }, [leaseStatus, leasePhase?.phase]);

  const hintOverride = useMemo(() => {
    if (leasePhase?.phase === 'already_seated') {
      return 'You are already in this experience in another tab — close it and this one will connect automatically.';
    }

    return null;
  }, [leasePhase?.phase]);

  const percentRef = useRef(0);
  const etaRef = useRef(PHASE_TAIL_SEC[0]);
  const phaseIdxRef = useRef(phaseIdx);
  const phaseStartedAtRef = useRef(performance.now());
  const readyRef = useRef(ready);
  const serverEtaRef = useRef<IServerEta | null>(null);
  const listenersRef = useRef(new Set<CinemaProgressListener>());

  const [percent, setPercent] = useState(0);
  const [etaSec, setEtaSec] = useState<number | null>(PHASE_TAIL_SEC[0]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    phaseIdxRef.current = phaseIdx;
    phaseStartedAtRef.current = performance.now();
  }, [phaseIdx]);

  useEffect(() => {
    readyRef.current = ready;

    if (!ready) {
      return;
    }

    // Both latched from `ready` directly rather than left for the rAF loop below to reach: that
    // loop can freeze or throttle on a hidden tab, which otherwise stalled `done` (and the entry
    // it gates) for as long as the tab stayed hidden, and left the bar visibly stuck below 100
    // while entry had already proceeded.
    setDone(true);
    percentRef.current = 100;
    setPercent(100);
    listenersRef.current.forEach((listener) => listener(100));
  }, [ready]);

  const serverPhase = leasePhase?.phase;
  const serverEtaSec = leasePhase?.etaSec;

  useEffect(() => {
    if (serverEtaSec == null) {
      return;
    }

    const key = `${serverPhase ?? '-'}:${serverEtaSec}`;

    if (serverEtaRef.current?.key === key) {
      return;
    }

    serverEtaRef.current = {
      key,
      valueSec: serverEtaSec,
      at: performance.now(),
    };
  }, [serverPhase, serverEtaSec]);

  const subscribe = useCallback((listener: CinemaProgressListener) => {
    listenersRef.current.add(listener);
    listener(percentRef.current);

    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (error) {
      return;
    }

    let frameId = 0;
    let previousAt = performance.now();

    const serverEtaLeftSec = (now: number): number | null => {
      const anchor = phaseIdxRef.current === 0 ? serverEtaRef.current : null;

      return anchor
        ? Math.max(0, anchor.valueSec - (now - anchor.at) / 1000)
        : null;
    };

    const remainingSec = (now: number): number => {
      const serverLeft = serverEtaLeftSec(now);

      if (serverLeft !== null) {
        return serverLeft + PHASE_TAIL_SEC[1];
      }

      return (
        PHASE_TAIL_SEC[phaseIdxRef.current] -
        (now - phaseStartedAtRef.current) / 1000
      );
    };

    const advance = (now: number, deltaSec: number): number => {
      const value = percentRef.current;

      // `ready` already snapped `percentRef.current` to 100 in the effect above — nothing left
      // for this loop to do once it gets there, it only still drives the pre-ready catch-up below.
      if (readyRef.current) {
        return value;
      }

      const phase = CINEMA_PHASES[phaseIdxRef.current];

      if (value < phase.threshold) {
        return Math.min(
          phase.threshold,
          value +
            (phase.threshold - value) *
              (1 - Math.exp(-CATCHUP_RATE_PER_SEC * deltaSec)) +
            CATCHUP_FLOOR_PER_SEC * deltaSec,
        );
      }

      const ceiling =
        (CINEMA_PHASES[phaseIdxRef.current + 1]?.threshold ?? 100) - 0.5;
      const pace = serverEtaLeftSec(now) ?? phase.expectedSec;
      const tau = Math.max(MIN_PHASE_TAU_SEC, pace / 3);

      return value + (ceiling - value) * (1 - Math.exp(-deltaSec / tau));
    };

    const frame = (now: number) => {
      const deltaSec = Math.min(
        MAX_FRAME_DELTA_SEC,
        Math.max(0, (now - previousAt) / 1000),
      );
      previousAt = now;

      const value = Math.min(
        100,
        Math.max(percentRef.current, advance(now, deltaSec)),
      );
      percentRef.current = value;
      listenersRef.current.forEach((listener) => listener(value));

      etaRef.current -= deltaSec;
      etaRef.current +=
        (remainingSec(now) - etaRef.current) *
        (1 - Math.exp(-deltaSec / ETA_GLIDE_TAU_SEC));

      const nextPercent = Math.floor(value);
      const nextEta =
        readyRef.current || etaRef.current < ETA_FLOOR_SEC
          ? null
          : Math.round(etaRef.current);

      setPercent((prev) => (prev === nextPercent ? prev : nextPercent));
      setEtaSec((prev) => (prev === nextEta ? prev : nextEta));

      if (value >= 100) {
        return;
      }

      frameId = requestAnimationFrame(frame);
    };

    frameId = requestAnimationFrame(frame);

    return () => cancelAnimationFrame(frameId);
  }, [error]);

  return {
    percent,
    subscribe,
    phaseIdx,
    ready,
    done,
    etaSec,
    error,
    hintOverride,
    onSpot,
  };
};
